import { fetchEventSource } from '@microsoft/fetch-event-source';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAuth0AccessTokenSilently, isAuth0Initialized } from '@store/auth0BaseQuery';
import type { AppDispatch } from '@store/index';
import { Mutex } from 'async-mutex';

import type { SseErrorEvent, WaitingRoomSseEvent } from '../types';

import { waitingRoomApi } from './api';
import { waitingRoomActions } from './slice';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;
const STREAM_URL = `${API_BASE_URL}/v2/provider/waiting-room/queue/subscribe`;
const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000] as const;

const mutex = new Mutex();

interface WaitingRoomStreamState {
  isActive: boolean;
}

class FatalStreamError extends Error {}

class ServerSentStreamError extends Error {
  constructor(readonly event: SseErrorEvent) {
    super(event.error);
  }
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const sleepUntilReconnectOrRemoved = (ms: number, cacheEntryRemoved: Promise<void>) =>
  Promise.race([sleep(ms), cacheEntryRemoved]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const parseSseEvent = (data: string): WaitingRoomSseEvent | null => {
  try {
    const parsed = JSON.parse(data) as unknown;
    if (!isRecord(parsed) || typeof parsed.type !== 'string') {
      return null;
    }
    return parsed as unknown as WaitingRoomSseEvent;
  } catch {
    return null;
  }
};

const refetchQueue = (dispatch: AppDispatch) => {
  void dispatch(
    waitingRoomApi.endpoints.getV2ProviderWaitingRoomQueue.initiate(undefined, {
      forceRefetch: true,
      subscribe: false,
    }),
  );
};

const handleWaitingRoomEvent = (event: WaitingRoomSseEvent, dispatch: AppDispatch) => {
  switch (event.type) {
    case 'queue_snapshot':
      dispatch(waitingRoomActions.queueSnapshotReceived(event));
      break;
    case 'provider_status_changed':
      dispatch(waitingRoomActions.providerStatusChanged(event));
      break;
    case 'patient_checked_in':
    case 'patient_admitted':
    case 'patient_removed':
    case 'patient_state_changed':
      dispatch(waitingRoomActions.patientStateChanged(event));
      refetchQueue(dispatch);
      break;
    case 'error':
      dispatch(waitingRoomActions.sseErrorReceived(event));
      throw new ServerSentStreamError(event);
  }
};

export const waitingRoomStreamingApi = createApi({
  reducerPath: 'telehealth/waitingRoom/streaming',
  baseQuery: fakeBaseQuery(),
  endpoints: builder => ({
    subscribeProviderWaitingRoomQueue: builder.query<WaitingRoomStreamState, undefined>({
      queryFn: () => ({
        data: {
          isActive: false,
        },
      }),

      async onCacheEntryAdded(
        _arg,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { updateCachedData, cacheEntryRemoved, dispatch },
      ) {
        let isRemoved = false;
        let activeController: AbortController | null = null;

        cacheEntryRemoved.then(() => {
          isRemoved = true;
          activeController?.abort();
        });

        let reconnectAttempt = 0;

        while (!isRemoved) {
          activeController = new AbortController();
          dispatch(
            reconnectAttempt === 0
              ? waitingRoomActions.sseConnecting()
              : waitingRoomActions.sseReconnecting(),
          );

          await mutex.waitForUnlock();

          let token: string;
          try {
            token = await getAuth0AccessTokenSilently();
          } catch {
            dispatch(waitingRoomActions.waitingRoomErrorReceived('Authentication failed.'));
            dispatch(waitingRoomActions.sseReconnecting('Authentication failed.'));
            await sleepUntilReconnectOrRemoved(
              RECONNECT_DELAYS_MS[Math.min(reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)],
              cacheEntryRemoved,
            );
            reconnectAttempt += 1;
            continue;
          }

          try {
            await fetchEventSource(STREAM_URL, {
              method: 'GET',
              signal: activeController.signal,
              openWhenHidden: true,
              headers: {
                Authorization: `Bearer ${token}`,
              },

              onopen: async response => {
                if (response.ok) {
                  reconnectAttempt = 0;
                  dispatch(waitingRoomActions.sseConnected());
                  updateCachedData(draft => {
                    draft.isActive = true;
                  });
                  return;
                }

                if (response.status === 401 && isAuth0Initialized()) {
                  if (!mutex.isLocked()) {
                    const release = await mutex.acquire();
                    try {
                      await getAuth0AccessTokenSilently({ cacheMode: 'off' });
                    } finally {
                      release();
                    }
                  } else {
                    await mutex.waitForUnlock();
                  }
                  throw new Error('waiting_room_stream_unauthorized');
                }

                if (response.status >= 400 && response.status < 500) {
                  throw new FatalStreamError(
                    `Waiting room stream rejected: ${response.status} ${response.statusText}`,
                  );
                }

                throw new Error(
                  `Waiting room stream failed: ${response.status} ${response.statusText}`,
                );
              },

              onmessage: message => {
                if (!message.data) {
                  return;
                }

                const event = parseSseEvent(message.data);
                if (!event) {
                  return;
                }

                handleWaitingRoomEvent(event, dispatch);
              },

              onclose: () => {
                updateCachedData(draft => {
                  draft.isActive = false;
                });
              },

              onerror: error => {
                throw error;
              },
            });
          } catch (error) {
            if (isRemoved || activeController.signal.aborted) {
              break;
            }

            const message =
              error instanceof Error ? error.message : 'Waiting room stream disconnected.';

            if (error instanceof FatalStreamError) {
              dispatch(waitingRoomActions.waitingRoomErrorReceived(message));
              dispatch(waitingRoomActions.sseDisconnected());
              break;
            }

            if (!(error instanceof ServerSentStreamError)) {
              dispatch(waitingRoomActions.sseReconnecting(message));
            }
          }

          if (!isRemoved) {
            const delayMs =
              RECONNECT_DELAYS_MS[Math.min(reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)];
            reconnectAttempt += 1;
            await sleepUntilReconnectOrRemoved(delayMs, cacheEntryRemoved);
          }
        }

        updateCachedData(draft => {
          draft.isActive = false;
        });
        dispatch(waitingRoomActions.sseDisconnected());
      },
    }),
  }),
});

export const { useSubscribeProviderWaitingRoomQueueQuery } = waitingRoomStreamingApi;
